import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  Plus,
  Search,
  Edit3,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Timer,
  X,
  ChevronRight,
  ChevronLeft,
  Eye,
  SlidersHorizontal,
  FileSpreadsheet,
  Upload,
  FileText,
  ArrowDownToLine,
  ArrowUpFromLine,
} from "lucide-react";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import StatCard from "../components/ui/StatCard";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import { toast } from "react-hot-toast";

function ArchivAdmin() {
  const [isSidebarOpen, setSidebarOpen] = useState(window.innerWidth > 1024);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // تبويب المودال: وارد / صادر
  const [movementTab, setMovementTab] = useState("وارد");
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  // لوحة الفلاتر المتقدمة المطوية
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  useEffect(() => {
    document.title =
      "أرشيف الإدارة - الإدارة العامة للعمليات والتوقيع الإلكتروني";
  }, []);

  // إعدادات القوائم المنسدلة (نوع الإدارة وجهة المستند) - قادمة من الباك إند لاحقاً
  const [settings, setSettings] = useState({
    departments: [], // نوع الإدارة
    entities: [], // جهة المستند
  });

  const [filters, setFilters] = useState({
    department: "",
    entity: "",
    movementType: "", // صادر / وارد
    financierName: "",
    registrationNumber: "",
    docStartDate: "",
    docEndDate: "",
    movementStartDate: "",
    movementEndDate: "",
  });

  const [newDoc, setNewDoc] = useState({
    id: null,
    financierName: "",
    registrationNumber: "",
    description: "",
    documentDate: "",
    departmentId: "",
    movementType: "",
    movementDate: "",
    entityId: "",
    file: null,
    fileName: "",
  });

  const fetchDepartments = async () => {
    try {
      const response = await fetch("/api/departments", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await response.json();
      setDepartments(data); // حفظ البيانات في الـ state
    } catch (error) {
      console.error("خطأ في جلب الإدارات:", error);
    }
  };

  useEffect(() => {
    fetchDepartments(); // إضافة دالة لجلب الإدارات
  }, []);

  const [formData, setFormData] = useState({
    fileNumber: "",
    name: "",
    birthDate: "",
    financialGrade: "",
    groupType: "",
    jobTitle: "",
    planCapacity: "",
    notes: "",
  });

  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const fetchInitialData = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        console.warn("لا يوجد توكن، يتم التحويل لصفحة الدخول");
        navigate("/login");
        return;
      }

      const authHeaders = {
        headers: { Authorization: `Bearer ${token}` },
      };

      try {
        setLoading(true);
        const [settingsRes, docsRes] = await Promise.all([
          axios.get("/api/document-settings", authHeaders),
          axios.get("/api/documents", authHeaders),
        ]);
        const data = settingsRes.data;

        setSettings({
          departments: data.departments || [],
          entities: data.documentParties || [],
        });

        // تحويل الـ flat fields القادمة من الـ SP لـ nested objects
        const mappedDocs = (docsRes.data || []).map((doc) => ({
          ...doc,
          department: { name: doc.departmentName || "" },
          entity: { name: doc.partyName || "" },
          fileUrl: doc.filePath ? `${doc.filePath}` : null,
        }));
        setDocuments(mappedDocs);
      } catch (err) {
        console.error("Error fetching data:", err);
        if (err.response?.status === 401) {
          localStorage.removeItem("token");
          navigate("/login");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  // الحسابات والفلترة الدقيقة
  const filteredDocs = documents.filter((doc) => {
    const matchesSearch =
      doc.id.toString().includes(searchTerm) ||
      (doc.description &&
        doc.description.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesDepartment =
      !filters.department || doc.department?.name === filters.department;
    const matchesEntity =
      !filters.entity || doc.entity?.name === filters.entity;
    const matchesMovementType =
      !filters.movementType || doc.movementType === filters.movementType;

    const matchesFinancierName =
      !filters.financierName ||
      (doc.financierName &&
        doc.financierName
          .toLowerCase()
          .includes(filters.financierName.toLowerCase()));

    const matchesRegistrationNumber =
      !filters.registrationNumber ||
      (doc.registrationNumber &&
        doc.registrationNumber.includes(filters.registrationNumber));

    // تصفير وقت تاريخ المستند للمقارنة العادلة باليوم
    const docDate = doc.documentDate ? new Date(doc.documentDate) : null;
    if (docDate) docDate.setHours(0, 0, 0, 0);

    const docStart = filters.docStartDate
      ? new Date(filters.docStartDate)
      : null;
    if (docStart) docStart.setHours(0, 0, 0, 0);

    const docEnd = filters.docEndDate ? new Date(filters.docEndDate) : null;
    if (docEnd) docEnd.setHours(0, 0, 0, 0);

    const matchesDocStart = !docStart || (docDate && docDate >= docStart);
    const matchesDocEnd = !docEnd || (docDate && docDate <= docEnd);

    // تصفير وقت تاريخ الصادر/الوارد
    const movementDate = doc.movementDate ? new Date(doc.movementDate) : null;
    if (movementDate) movementDate.setHours(0, 0, 0, 0);

    const movStart = filters.movementStartDate
      ? new Date(filters.movementStartDate)
      : null;
    if (movStart) movStart.setHours(0, 0, 0, 0);

    const movEnd = filters.movementEndDate
      ? new Date(filters.movementEndDate)
      : null;
    if (movEnd) movEnd.setHours(0, 0, 0, 0);

    const matchesMovStart =
      !movStart || (movementDate && movementDate >= movStart);
    const matchesMovEnd = !movEnd || (movementDate && movementDate <= movEnd);

    return (
      matchesSearch &&
      matchesDepartment &&
      matchesEntity &&
      matchesMovementType &&
      matchesFinancierName &&
      matchesRegistrationNumber &&
      matchesDocStart &&
      matchesDocEnd &&
      matchesMovStart &&
      matchesMovEnd
    );
  });

  const downloadExcel = () => {
    const dataToExport = filteredDocs.length > 0 ? filteredDocs : documents;

    if (dataToExport.length === 0) {
      alert("لا توجد بيانات لتصديرها حالياً");
      return;
    }

    const mappedData = dataToExport.map((item, index) => ({
      م: index + 1,
      "اسم الممول": item.financierName || "---",
      "رقم التسجيل": item.registrationNumber || "---",
      "وصف المستند": item.description,
      "تاريخ المستند": item.documentDate
        ? new Date(item.documentDate).toLocaleDateString("ar-EG")
        : "---",
      "نوع الإدارة": item.department?.name || "---",
      "نوع الحركة": item.movementType || "---",
      "تاريخ الصادر/الوارد": item.movementDate
        ? new Date(item.movementDate).toLocaleDateString("ar-EG")
        : "---",
      "جهة المستند": item.entity?.name || "---",
      "اسم الملف": item.fileName || "---",
    }));

    const worksheet = XLSX.utils.json_to_sheet(mappedData);
    worksheet["!dir"] = "rtl";
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "أرشيف المستندات");
    XLSX.writeFile(
      workbook,
      `تقرير_الأرشيف_${new Date().toLocaleDateString("ar-EG")}.xlsx`,
    );
  };

  const handleDelete = async (id) => {
    if (window.confirm("هل أنت متأكد من حذف هذا المستند؟")) {
      try {
        await axios.delete(`/api/documents/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setDocuments(documents.filter((doc) => doc.id !== id));
        toast.success("تم الحذف بنجاح");
      } catch (error) {
        toast.error("فشل في الحذف");
      }
    }
  };

  const handleView = (doc) => {
    setSelectedDoc(doc);
    setIsViewOpen(true);
  };

  const handleEdit = (doc) => {
    const docMovementType = doc.movementType || "وارد";
    setMovementTab(docMovementType);
    setNewDoc({
      id: doc.id,
      financierName: doc.financierName || "",
      registrationNumber: doc.registrationNumber || "",
      description: doc.description || "",
      documentDate: doc.documentDate ? doc.documentDate.split("T")[0] : "",
      departmentId: doc.departmentId || "",
      movementType: docMovementType,
      movementDate: doc.movementDate ? doc.movementDate.split("T")[0] : "",
      entityId: doc.partyName || "",
      file: null,
      fileName: doc.fileName || "",
    });
    setIsModalOpen(true);
  };

  const resetForm = (tab = "وارد") => {
    setMovementTab(tab);
    setNewDoc({
      id: null,
      financierName: "",
      registrationNumber: "",
      description: "",
      documentDate: "",
      departmentId: "",
      movementType: tab,
      movementDate: "",
      entityId: "",
      file: null,
      fileName: "",
    });
  };

  // تبديل التبويب (وارد/صادر) داخل المودال مع تحديث نوع الحركة في الفورم
  const handleMovementTabChange = (tab) => {
    setMovementTab(tab);
    setNewDoc((prev) => ({ ...prev, movementType: tab }));
  };

  // الحد الأقصى لحجم الملف المرفوع: 5 ميجابايت
  const MAX_FILE_SIZE = 5 * 1024 * 1024;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error("حجم الملف يتجاوز الحد الأقصى المسموح به (5 ميجابايت)");
      e.target.value = "";
      return;
    }

    setNewDoc((prev) => ({ ...prev, file, fileName: file.name }));
  };

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const sortedDocs = [...filteredDocs].sort((a, b) => a.id - b.id);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedDocs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedDocs.length / itemsPerPage);

  const handleAddDoc = async (e) => {
    e.preventDefault();
    try {
      if (newDoc.file && newDoc.file.size > MAX_FILE_SIZE) {
        toast.error("حجم الملف يتجاوز الحد الأقصى المسموح به (5 ميجابايت)");
        return;
      }

      const formData = new FormData();
      formData.append("financierName", newDoc.financierName);
      formData.append("registrationNumber", newDoc.registrationNumber);
      formData.append("description", newDoc.description);
      formData.append("documentDate", newDoc.documentDate);
      formData.append("departmentId", newDoc.departmentId);
      formData.append("movementType", movementTab);
      // نوع الحركة الآن محدد من التبويب، وتاريخ الحركة يتبع تاريخ المستند
      formData.append("movementDate", newDoc.movementDate || newDoc.documentDate);
      formData.append("partyName", newDoc.entityId);
      if (newDoc.file) {
        formData.append("file", newDoc.file);
      }

      const headers = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };

      if (newDoc.id) {
        await axios.put(
          `/api/documents/${newDoc.id}`,
          formData,
          { headers },
        );
      } else {
        await axios.post("/api/documents", formData, {
          headers,
        });
      }

      toast.success("تم الحفظ بنجاح");
      setIsModalOpen(false);
      await fetchDocuments();
    } catch (error) {
      toast.error("حدث خطأ أثناء الحفظ");
    }
  };

  const fetchDocuments = async () => {
    const token = localStorage.getItem("token");
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    const docsRes = await axios.get(
      "/api/documents",
      authHeaders,
    );
    const mappedDocs = (docsRes.data || []).map((doc) => ({
      ...doc,
      department: { name: doc.departmentName || "" },
      entity: { name: doc.partyName || "" },
      fileUrl: doc.filePath ? `${doc.filePath}` : null,
    }));
    setDocuments(mappedDocs);
  };

  return (
    <div className="flex min-h-screen bg-[#F8F9FA] font-ibm" dir="rtl">
      <Sidebar
        isOpen={isSidebarOpen}
        toggleSidebar={() => setSidebarOpen(!isSidebarOpen)}
      />

      <div
        className={`flex-1 transition-all duration-300 p-4 md:p-8 overflow-y-auto ${isSidebarOpen ? "lg:mr-64" : "lg:mr-20"}`}
      >
        <Header
          isSidebarOpen={isSidebarOpen}
          setSidebarOpen={setSidebarOpen}
          title="أرشيف الإدارة"
        />

        {/* الترحيب وإضافة مستند جديد */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-ibm font-extrabold text-[#546E7A]">
              أرشيف الإدارة
            </h2>
            <p className="text-gray-400 font-ibm mt-1">
              يمكنك إضافة ومتابعة المستندات الصادرة والواردة وتفاصيلها بدقة.
            </p>
          </div>
          <button
            onClick={() => {
              resetForm("وارد");
              setIsModalOpen(true);
            }}
            className="bg-[#546E7A] hover:bg-[#263238] text-white px-8 py-3 rounded-2xl font-bold flex items-center gap-2 transition shadow-lg w-full md:w-auto justify-center"
          >
            <Plus size={20} /> إضافة مستند جديد
          </button>
        </div>

        {/* الكروت الإحصائية */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          <StatCard
            title="إجمالي المستندات"
            value={documents.length}
            icon={<AlertCircle size={40} className="text-[#546E7A]" />}
            badge="الكل"
            badgeColor="text-blue-500 bg-blue-50"
          />
          <StatCard
            title="مستندات صادرة"
            value={documents.filter((d) => d.movementType === "صادر").length}
            icon={<ArrowUpFromLine size={40} className="text-[#546E7A]" />}
            badge="صادر"
            badgeColor="text-green-500 bg-green-50"
          />
          <StatCard
            title="مستندات واردة"
            value={documents.filter((d) => d.movementType === "وارد").length}
            icon={<ArrowDownToLine size={40} className="text-[#546E7A]" />}
            badge="وارد"
            badgeColor="text-orange-500 bg-orange-50"
          />
        </div>

        {/* القسم الرئيسي للجدول والبحث بتحديثات الـ UX الكاملة والـ Floating Labels */}
        <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden">
          {/* شريط الإجراءات والبحث الفوقي المتوازن */}
          <div className="p-6 border-b border-gray-100 bg-gray-50/30 space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              {/* حقل البحث الذكي بـ Floating Label ناعم */}
              <div className="relative w-full sm:w-1/2 md:w-1/3 group">
                <Search
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#546E7A] transition-colors z-20"
                  size={18}
                />
                <input
                  type="text"
                  id="floatingSearch"
                  placeholder=" "
                  className="block w-full pr-11 pl-4 pt-6 pb-2 text-sm text-right bg-white border border-gray-200 rounded-2xl outline-none focus:border-[#546E7A] focus:ring-2 focus:ring-[#546E7A]/10 transition-all text-[#263238] font-medium peer"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <label
                  htmlFor="floatingSearch"
                  className="absolute text-sm text-gray-400 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[right] right-11 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3 peer-focus:text-[#546E7A] pointer-events-none"
                >
                  البحث برقم المستند أو الوصف...
                </label>
              </div>

              {/* أزرار التحكم الجانبية */}
              <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                <button
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold border transition-all ${showAdvancedFilters ? "bg-[#546E7A] text-white border-[#546E7A]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
                >
                  <SlidersHorizontal size={16} />
                  <span>تصفية متقدمة</span>
                </button>
                <button
                  onClick={downloadExcel}
                  className="flex items-center gap-2 px-5 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-bold transition shadow-md"
                >
                  <FileSpreadsheet size={16} />
                  <span>تصدير Excel</span>
                </button>
              </div>
            </div>

            {/* لوحة الفلاتر المتقدمة بـ Floating Labels ناعمة ومستقرة */}
            <div
              className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t border-gray-100 transition-all duration-300 origin-top ${showAdvancedFilters ? "opacity-100 scale-y-100 h-auto" : "opacity-0 scale-y-0 h-0 overflow-hidden pointer-events-none !pt-0 !border-t-0"}`}
            >
              {/* نوع الإدارة */}
              <div className="relative">
                <select
                  id="departmentId"
                  value={formData.departmentId || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      departmentId: e.target.value,
                    })
                  }
                  className="block w-full p-4 pt-5 text-right bg-gray-50 border border-gray-100 rounded-2xl text-sm focus:bg-white focus:border-[#546E7A] outline-none transition-all"
                >
                  <option value="">اختر الإدارة</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
                <label className="absolute text-[10px] text-gray-400 top-1 right-2">
                  الإدارة
                </label>
              </div>

              {/* جهة المستند */}
              <div className="relative group">
                <select
                  id="filterEntity"
                  className="block w-full p-4 pt-6 pb-2 text-sm text-right bg-white border border-gray-200 rounded-xl outline-none focus:border-[#546E7A] transition-all peer appearance-none cursor-pointer text-[#263238] font-medium"
                  value={filters.entity}
                  onChange={(e) =>
                    setFilters({ ...filters, entity: e.target.value })
                  }
                >
                  <option value="">الكل</option>
                  {settings.entities.map((en) => (
                    <option key={en.id} value={en.name}>
                      {en.name}
                    </option>
                  ))}
                </select>
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 peer-focus:text-[#546E7A]">
                  <ChevronDown size={16} />
                </div>
                <label
                  htmlFor="filterEntity"
                  className="absolute text-xs text-gray-400 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[right] right-4 pointer-events-none peer-focus:text-[#546E7A]"
                >
                  جهة المستند
                </label>
              </div>

              {/* اسم الممول */}
              <div className="relative group">
                <input
                  type="text"
                  id="filterFinancierName"
                  placeholder=" "
                  className="peer block w-full p-4 pt-6 pb-2 text-sm text-right bg-white border border-gray-200 rounded-xl outline-none focus:border-[#546E7A] transition-all text-[#263238] font-medium"
                  value={filters.financierName}
                  onChange={(e) =>
                    setFilters({ ...filters, financierName: e.target.value })
                  }
                />
                <label
                  htmlFor="filterFinancierName"
                  className="absolute text-xs text-gray-400 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[right] right-4 pointer-events-none peer-focus:text-[#546E7A]"
                >
                  اسم الممول
                </label>
              </div>

              {/* رقم التسجيل */}
              <div className="relative group">
                <input
                  type="text"
                  id="filterRegistrationNumber"
                  placeholder=" "
                  inputMode="numeric"
                  maxLength={9}
                  className="peer block w-full p-4 pt-6 pb-2 text-sm text-right bg-white border border-gray-200 rounded-xl outline-none focus:border-[#546E7A] transition-all text-[#263238] font-medium"
                  value={filters.registrationNumber}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      registrationNumber: e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 9),
                    })
                  }
                />
                <label
                  htmlFor="filterRegistrationNumber"
                  className="absolute text-xs text-gray-400 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[right] right-4 pointer-events-none peer-focus:text-[#546E7A]"
                >
                  رقم التسجيل
                </label>
              </div>

              {/* نوع الحركة */}
              <div className="relative group">
                <select
                  id="filterMovementType"
                  className="block w-full p-4 pt-6 pb-2 text-sm text-right bg-white border border-gray-200 rounded-xl outline-none focus:border-[#546E7A] transition-all peer appearance-none cursor-pointer text-[#263238] font-medium"
                  value={filters.movementType}
                  onChange={(e) =>
                    setFilters({ ...filters, movementType: e.target.value })
                  }
                >
                  <option value="">الكل</option>
                  <option value="صادر">صادر</option>
                  <option value="وارد">وارد</option>
                </select>
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 peer-focus:text-[#546E7A]">
                  <ChevronDown size={16} />
                </div>
                <label
                  htmlFor="filterMovementType"
                  className="absolute text-xs text-gray-400 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[right] right-4 pointer-events-none peer-focus:text-[#546E7A]"
                >
                  نوع الحركة
                </label>
              </div>

              {/* حقول التواريخ المتقدمة */}
              <div className="md:col-span-2 lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                <div className="relative">
                  <input
                    type="date"
                    id="docStart"
                    className="block w-full p-4 pt-6 pb-2 text-sm text-center bg-gray-50 border border-gray-200 rounded-xl outline-none focus:bg-white focus:border-[#546E7A] transition-all text-gray-700 font-ibm peer"
                    value={filters.docStartDate}
                    onChange={(e) =>
                      setFilters({ ...filters, docStartDate: e.target.value })
                    }
                  />
                  <label
                    htmlFor="docStart"
                    className="absolute text-[11px] text-[#546E7A] duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[right] right-4 pointer-events-none"
                  >
                    تاريخ المستند من
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="date"
                    id="docEnd"
                    className="block w-full p-4 pt-6 pb-2 text-sm text-center bg-gray-50 border border-gray-200 rounded-xl outline-none focus:bg-white focus:border-[#546E7A] transition-all text-gray-700 font-ibm peer"
                    value={filters.docEndDate}
                    onChange={(e) =>
                      setFilters({ ...filters, docEndDate: e.target.value })
                    }
                  />
                  <label
                    htmlFor="docEnd"
                    className="absolute text-[11px] text-[#546E7A] duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[right] right-4 pointer-events-none"
                  >
                    تاريخ المستند إلى
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="date"
                    id="movStart"
                    className="block w-full p-4 pt-6 pb-2 text-sm text-center bg-gray-50 border border-gray-200 rounded-xl outline-none focus:bg-white focus:border-[#546E7A] transition-all text-gray-700 font-ibm peer"
                    value={filters.movementStartDate}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        movementStartDate: e.target.value,
                      })
                    }
                  />
                  <label
                    htmlFor="movStart"
                    className="absolute text-[11px] text-[#546E7A] duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[right] right-4 pointer-events-none"
                  >
                    تاريخ الصادر/الوارد من
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="date"
                    id="movEnd"
                    className="block w-full p-4 pt-6 pb-2 text-sm text-center bg-gray-50 border border-gray-200 rounded-xl outline-none focus:bg-white focus:border-[#546E7A] transition-all text-gray-700 font-ibm peer"
                    value={filters.movementEndDate}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        movementEndDate: e.target.value,
                      })
                    }
                  />
                  <label
                    htmlFor="movEnd"
                    className="absolute text-[11px] text-[#546E7A] duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[right] right-4 pointer-events-none"
                  >
                    تاريخ الصادر/الوارد إلى
                  </label>
                </div>
              </div>

              {/* زر مسح الفلاتر */}
              <div className="md:col-span-2 lg:col-span-3 flex justify-end pt-2">
                <button
                  onClick={() =>
                    setFilters({
                      department: "",
                      entity: "",
                      movementType: "",
                      financierName: "",
                      registrationNumber: "",
                      docStartDate: "",
                      docEndDate: "",
                      movementStartDate: "",
                      movementEndDate: "",
                    })
                  }
                  className="px-6 py-2 bg-red-50 hover:bg-red-100 text-red-500 rounded-xl font-bold transition text-xs flex items-center gap-1"
                >
                  <X size={14} />
                  <span>تفريغ كافة الفلاتر</span>
                </button>
              </div>
            </div>
          </div>

          {/* جدول البيانات الرئيسي */}
          <div className="overflow-x-auto">
            <table className="w-full text-right border-collapse">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 text-xs border-b border-gray-50">
                  <th className="px-4 py-5 font-medium">م</th>
                  <th className="px-4 py-5 font-medium">اسم الممول</th>
                  <th className="px-4 py-5 font-medium">رقم التسجيل</th>
                  <th className="px-4 py-5 font-medium">وصف المستند</th>
                  <th className="px-4 py-5 font-medium">تاريخ المستند</th>
                  <th className="px-4 py-5 font-medium">نوع الإدارة</th>
                  <th className="px-4 py-5 font-medium text-center">
                    نوع الحركة
                  </th>
                  <th className="px-4 py-5 font-medium">تاريخ الصادر/الوارد</th>
                  <th className="px-4 py-5 font-medium">جهة المستند</th>
                  <th className="px-4 py-5 font-medium text-center">الملف</th>
                  <th className="px-4 py-5 font-medium text-center">إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {currentItems.map((doc, index) => (
                  <tr
                    key={doc.id}
                    className="hover:bg-gray-50/60 transition-colors group text-sm"
                  >
                    <td className="px-4 py-4 font-ibm text-gray-400">
                      #{indexOfFirstItem + index + 1}
                    </td>
                    <td
                      className="px-4 py-4 max-w-[140px] truncate text-[#263238]"
                      title={doc.financierName}
                    >
                      {doc.financierName || "-"}
                    </td>
                    <td className="px-4 py-4 font-ibm text-gray-500 text-xs">
                      {doc.registrationNumber || "-"}
                    </td>
                    <td
                      className="px-4 py-4 max-w-[160px] truncate font-bold text-[#263238]"
                      title={doc.description}
                    >
                      {doc.description}
                    </td>
                    <td className="px-4 py-4 text-gray-500 font-ibm text-xs">
                      {doc.documentDate
                        ? new Date(doc.documentDate).toLocaleDateString("en-CA")
                        : "-"}
                    </td>
                    <td className="px-4 py-4 text-[#263238]">
                      {doc.department?.name}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span
                        className={`px-3 py-1 rounded-full text-[10px] font-bold ${doc.movementType === "صادر" ? "bg-green-50 text-green-600" : "bg-blue-50 text-blue-600"}`}
                      >
                        {doc.movementType || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-gray-500 font-ibm text-xs">
                      {doc.movementDate
                        ? new Date(doc.movementDate).toLocaleDateString("en-CA")
                        : "-"}
                    </td>
                    <td className="px-4 py-4 text-[#263238]">
                      {doc.entity?.name}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {doc.fileUrl ? (
                        <a
                          href={doc.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-400 hover:text-[#546E7A] hover:bg-gray-100 rounded-lg transition inline-flex"
                          title={doc.fileName}
                        >
                          <FileText size={16} />
                        </a>
                      ) : (
                        <span className="text-gray-300 text-xs">لا يوجد</span>
                      )}
                    </td>
                    <td className="px-4 py-4 flex justify-center gap-1">
                      <button
                        onClick={() => handleView(doc)}
                        className="p-2 text-gray-400 hover:text-green-500 hover:bg-green-50 rounded-lg transition"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => handleEdit(doc)}
                        className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}

                {currentItems.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-10 text-center text-gray-400 text-sm"
                    >
                      لا توجد مستندات مطابقة لمعايير البحث الحالية
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* شريط تفاصيل العرض عند الضغط على العين */}
          {isViewOpen && selectedDoc && (
            <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
              <div
                className="absolute inset-0 bg-[#263238]/40 backdrop-blur-sm"
                onClick={() => setIsViewOpen(false)}
              ></div>
              <div className="relative bg-white w-full max-w-4xl rounded-[2.5rem] shadow-2xl overflow-hidden p-8 animate-in zoom-in duration-200">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-3">
                    <h3 className="text-xl font-bold text-[#263238]">
                      تفاصيل المستند
                    </h3>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-bold ${selectedDoc.movementType === "صادر" ? "bg-green-100 text-green-600" : "bg-blue-100 text-blue-600"}`}
                    >
                      {selectedDoc.movementType || "-"}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsViewOpen(false)}
                    className="p-2 hover:bg-gray-100 rounded-full text-gray-400"
                  >
                    <X size={24} />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div className="relative col-span-1">
                      <input
                        value={selectedDoc.financierName || ""}
                        readOnly
                        className="block w-full p-2 pt-5 text-right bg-gray-50 border border-gray-100 rounded-xl text-xs font-bold"
                      />
                      <label className="absolute text-[10px] text-gray-400 top-1 right-2">
                        اسم الممول
                      </label>
                    </div>
                    <div className="relative col-span-1">
                      <input
                        value={selectedDoc.registrationNumber || ""}
                        readOnly
                        className="block w-full p-2 pt-5 text-right bg-gray-50 border border-gray-100 rounded-xl text-xs font-bold"
                      />
                      <label className="absolute text-[10px] text-gray-400 top-1 right-2">
                        رقم التسجيل
                      </label>
                    </div>
                    <div className="relative col-span-1">
                      <input
                        value={selectedDoc.department?.name || ""}
                        readOnly
                        className="block w-full p-2 pt-5 text-right bg-gray-50 border border-gray-100 rounded-xl text-xs font-bold"
                      />
                      <label className="absolute text-[10px] text-gray-400 top-1 right-2">
                        نوع الإدارة
                      </label>
                    </div>
                    <div className="relative col-span-1">
                      <input
                        value={selectedDoc.entity?.name || ""}
                        readOnly
                        className="block w-full p-2 pt-5 text-right bg-gray-50 border border-gray-100 rounded-xl text-xs"
                      />
                      <label className="absolute text-[10px] text-gray-400 top-1 right-2">
                        جهة المستند
                      </label>
                    </div>
                    <div className="relative col-span-1">
                      <input
                        value={
                          selectedDoc.documentDate
                            ? selectedDoc.documentDate.split("T")[0]
                            : "---"
                        }
                        readOnly
                        className="block w-full p-2 pt-5 text-center bg-gray-50 border border-gray-100 rounded-xl text-xs text-[#546E7A]"
                      />
                      <label className="absolute text-[10px] text-gray-400 top-1 right-2">
                        تاريخ المستند
                      </label>
                    </div>
                    <div className="relative col-span-1">
                      <input
                        value={
                          selectedDoc.movementDate
                            ? selectedDoc.movementDate.split("T")[0]
                            : "---"
                        }
                        readOnly
                        className="block w-full p-2 pt-5 text-center bg-gray-50 border border-gray-100 rounded-xl text-xs text-[#546E7A]"
                      />
                      <label className="absolute text-[10px] text-gray-400 top-1 right-2">
                        تاريخ الصادر/الوارد
                      </label>
                    </div>
                  </div>

                  <div className="relative">
                    <textarea
                      value={selectedDoc.description || ""}
                      readOnly
                      rows={3}
                      className="block w-full p-3 pt-5 text-right bg-gray-50 border border-gray-100 rounded-xl resize-none text-sm"
                    />
                    <label className="absolute text-xs text-gray-400 top-1 right-3">
                      وصف المستند
                    </label>
                  </div>

                  <div className="relative">
                    <div className="block w-full p-3 pt-5 text-right bg-gray-50 border border-gray-100 rounded-xl text-sm">
                      {selectedDoc.fileUrl ? (
                        <a
                          href={selectedDoc.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#546E7A] font-bold flex items-center gap-2 justify-end"
                        >
                          {selectedDoc.fileName || "عرض الملف"}
                          <FileText size={16} />
                        </a>
                      ) : (
                        <span className="text-gray-400">لا يوجد ملف مرفق</span>
                      )}
                    </div>
                    <label className="absolute text-xs text-gray-400 top-1 right-3">
                      الملف المرفق
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* الترقيم التلقائي للملف والصفحات */}
          <div className="flex items-center justify-between px-8 py-4 bg-gray-50/30 border-t border-gray-50">
            <div className="text-xs text-gray-500 font-ibm">
              عرض{" "}
              <span className="font-bold text-[#546E7A]">
                {sortedDocs.length === 0 ? 0 : indexOfFirstItem + 1}
              </span>{" "}
              إلى{" "}
              <span className="font-bold text-[#546E7A]">
                {Math.min(indexOfLastItem, sortedDocs.length)}
              </span>{" "}
              من أصل{" "}
              <span className="font-bold text-[#546E7A]">
                {sortedDocs.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`p-2 rounded-xl border transition-all ${currentPage === 1 ? "text-gray-200 border-gray-100" : "bg-white text-[#546E7A] border-gray-200 hover:border-[#546E7A]"}`}
              >
                <ChevronRight size={18} />
              </button>
              <div className="flex gap-1">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${currentPage === i + 1 ? "bg-[#546E7A] text-white" : "bg-white text-gray-400 hover:bg-gray-50"}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages || totalPages === 0}
                className={`p-2 rounded-xl border transition-all ${currentPage === totalPages || totalPages === 0 ? "text-gray-200 border-gray-100" : "bg-white text-[#546E7A] border-gray-200 hover:border-[#546E7A]"}`}
              >
                <ChevronLeft size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Modal الإضافة والتعديل الأساسي */}
        {isModalOpen && (
          <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-[#263238]/40 backdrop-blur-sm"
              onClick={() => setIsModalOpen(false)}
            ></div>
            <div className="relative bg-white w-full max-w-2xl rounded-[2.5rem] shadow-2xl p-8 animate-in zoom-in duration-200 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-xl font-bold text-[#263238]">
                  {newDoc.id ? "تعديل بيانات المستند" : "إضافة مستند جديد"}
                </h3>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-full text-gray-400"
                >
                  <X size={24} className="text-gray-400" />
                </button>
              </div>

              {/* تبويب وارد / صادر */}
              <div className="flex gap-0 border-b border-gray-100 mb-6">
                <button
                  type="button"
                  onClick={() => handleMovementTabChange("وارد")}
                  className={`px-6 py-3 text-sm font-bold transition-all border-b-2 flex items-center gap-2 ${
                    movementTab === "وارد"
                      ? "text-[#546E7A] border-[#546E7A]"
                      : "text-gray-400 border-transparent hover:text-gray-600"
                  }`}
                >
                  <ArrowDownToLine size={16} /> وارد
                </button>
                <button
                  type="button"
                  onClick={() => handleMovementTabChange("صادر")}
                  className={`px-6 py-3 text-sm font-bold transition-all border-b-2 flex items-center gap-2 ${
                    movementTab === "صادر"
                      ? "text-[#546E7A] border-[#546E7A]"
                      : "text-gray-400 border-transparent hover:text-gray-600"
                  }`}
                >
                  <ArrowUpFromLine size={16} /> صادر
                </button>
              </div>

              <form onSubmit={handleAddDoc} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* اسم الممول */}
                  <div className="relative group">
                    <input
                      type="text"
                      id="financierName"
                      required
                      placeholder=" "
                      className="peer w-full px-5 py-4 rounded-2xl border border-gray-100 bg-gray-50 focus:bg-white focus:border-[#546E7A] outline-none transition-all pt-7 text-right"
                      value={newDoc.financierName}
                      onChange={(e) =>
                        setNewDoc({ ...newDoc, financierName: e.target.value })
                      }
                    />
                    <label
                      htmlFor="financierName"
                      className="absolute text-sm font-bold text-gray-500 duration-300 transform -translate-y-4 scale-75 top-4 z-10 origin-[right] right-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#546E7A] pointer-events-none"
                    >
                      اسم الممول
                    </label>
                  </div>

                  {/* رقم التسجيل */}
                  <div className="relative group">
                    <input
                      type="text"
                      id="registrationNumber"
                      required
                      placeholder=" "
                      inputMode="numeric"
                      maxLength={9}
                      pattern="\d{9}"
                      title="رقم التسجيل يجب أن يتكون من 9 أرقام فقط"
                      className="peer w-full px-5 py-4 rounded-2xl border border-gray-100 bg-gray-50 focus:bg-white focus:border-[#546E7A] outline-none transition-all pt-7 text-right"
                      value={newDoc.registrationNumber}
                      onChange={(e) => {
                        const digitsOnly = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 9);
                        setNewDoc({
                          ...newDoc,
                          registrationNumber: digitsOnly,
                        });
                      }}
                    />
                    <label
                      htmlFor="registrationNumber"
                      className="absolute text-sm font-bold text-gray-500 duration-300 transform -translate-y-4 scale-75 top-4 z-10 origin-[right] right-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#546E7A] pointer-events-none"
                    >
                      رقم التسجيل 
                    </label>
                  </div>

                  {/* وصف المستند */}
                  <div className="relative md:col-span-2">
                    <textarea
                      id="description"
                      rows="2"
                      placeholder=" "
                      className="peer w-full px-5 py-4 rounded-2xl border border-gray-100 bg-gray-50 focus:bg-white focus:border-[#546E7A] outline-none transition-all pt-7 text-right"
                      value={newDoc.description}
                      onChange={(e) =>
                        setNewDoc({ ...newDoc, description: e.target.value })
                      }
                      required
                    />
                    <label
                      htmlFor="description"
                      className="absolute text-sm  font-bold text-gray-500 duration-300 transform -translate-y-4 scale-75 top-4 z-10 origin-[right] right-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#546E7A] pointer-events-none"
                    >
                      وصف المستند
                    </label>
                  </div>

                  {/* تاريخ المستند */}
                  <div className="relative group">
                    <input
                      type="date"
                      id="documentDate"
                      required
                      className="peer w-full px-5 py-4 rounded-2xl border border-gray-100 bg-gray-50 focus:bg-white focus:border-[#546E7A] outline-none transition-all pt-7 text-right"
                      value={newDoc.documentDate}
                      onChange={(e) =>
                        setNewDoc({ ...newDoc, documentDate: e.target.value })
                      }
                    />
                    <label
                      htmlFor="documentDate"
                      className="absolute text-sm font-bold text-gray-500 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[100%] right-5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3 peer-focus:text-[#546E7A]"
                    >
                      تاريخ المستند
                    </label>
                  </div>

                  {/* نوع الإدارة */}
                  <div className="relative">
                    <select
                      id="departmentId"
                      value={newDoc.departmentId || ""}
                      onChange={(e) =>
                        setNewDoc({ ...newDoc, departmentId: e.target.value })
                      }
                      className="peer w-full px-5 py-4 rounded-2xl border border-gray-100 bg-gray-50 focus:bg-white focus:border-[#546E7A] outline-none transition-all pt-7 text-right appearance-none"
                      required
                    >
                      <option value="" disabled hidden></option>
                      {departments.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                    <label
                      className={`absolute text-sm  font-bold text-gray-500 duration-300 transform z-10 origin-[right] right-4 pointer-events-none ${newDoc.movementType ? "-translate-y-4 scale-75 top-4" : "translate-y-0 scale-100 top-4"} peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#546E7A]`}
                    >
                      الإدارة
                    </label>
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 peer-focus:text-[#546E7A]">
                      <ChevronDown size={18} />
                    </div>
                  </div>
                </div>
                {/* جهة المستند */}
                <div className="relative group">
                  <input
                    type="text"
                    id="entity"
                    required
                    placeholder=" "
                    className="peer w-full px-5 py-4 rounded-2xl border border-gray-100 bg-gray-50 focus:bg-white focus:border-[#546E7A] outline-none transition-all pt-7 text-right"
                    value={newDoc.entityId}
                    onChange={(e) =>
                      setNewDoc({ ...newDoc, entityId: e.target.value })
                    }
                  />
                  <label
                    htmlFor="entity"
                    className={`absolute text-sm font-bold text-gray-500 duration-300 transform z-10 origin-[right] right-4 pointer-events-none ${
                      newDoc.entityId
                        ? "-translate-y-4 scale-75 top-4"
                        : "translate-y-0 scale-100 top-4 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:scale-75 peer-focus:-translate-y-4"
                    } peer-focus:text-[#546E7A]`}
                  >
                    جهة المستند
                  </label>
                </div>

                {/* رفع ملف */}
                <div className="space-y-2">
                  <label className="block text-sm font-bold text-[#546E7A] mr-2">
                    رفع مستند (PDF أو صور) - الحد الأقصى 5 ميجابايت
                  </label>
                  <div className="relative group">
                    <input
                      type="file"
                      id="fileUpload"
                      className="hidden"
                      accept=".pdf,image/*"
                      onChange={handleFileChange}
                    />
                    <label
                      htmlFor="fileUpload"
                      className="flex flex-col items-center justify-center w-full p-6 border-2 border-dashed border-gray-200 rounded-2xl bg-gray-50 hover:bg-gray-100 hover:border-[#546E7A] transition-all cursor-pointer group"
                    >
                      <Upload className="w-10 h-10 text-gray-500 group-hover:text-[#546E7A] mb-3 transition-colors" />
                      <p className="text-sm text-gray-500">
                        {newDoc.fileName ? (
                          <span className="text-[#546E7A] font-bold">
                            تم اختيار: {newDoc.fileName}
                          </span>
                        ) : (
                          "اضغط لرفع المستند أو اسحبه هنا"
                        )}
                      </p>
                    </label>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    className="flex-[2] bg-[#546E7A] text-white font-bold py-4 rounded-2xl hover:bg-[#263238] transition shadow-lg active:scale-[0.98]"
                  >
                    حفظ المستند
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 bg-gray-100 text-gray-500 font-bold py-4 rounded-2xl"
                  >
                    إلغاء
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ArchivAdmin;